/**
 * useVoice — shared voice helpers for the AI chat widgets.
 *
 * Two pieces:
 *   1. `speak(text)` — POST /api/voice/tts → MP3 blob → play via
 *      <audio>. Cached server-side so repeats are instant.
 *   2. `useDictation()` — wraps the browser's Web Speech API
 *      (SpeechRecognition) for press-and-hold dictation. Free,
 *      on-device, no backend cost. Falls back gracefully when
 *      the browser doesn't support it (Safari ≤16 needs the
 *      WebKit prefix).
 */
import { useCallback, useEffect, useRef, useState } from "react";


export function useTts() {
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);

  const speak = useCallback(async (text) => {
    if (!text || speaking) return;
    setSpeaking(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(`${base}/api/voice/tts`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body:    JSON.stringify({ text: text.slice(0, 3500) }),
      });
      if (!resp.ok) throw new Error(`TTS HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) {
      console.warn("TTS failed:", e);
      setSpeaking(false);
    }
  }, [speaking]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setSpeaking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  return { speak, stop, speaking };
}


/**
 * useDictation — browser STT via Web Speech API.
 *
 *   const { listening, transcript, start, stop, supported } =
 *     useDictation();
 *
 * Call `start()` on mouse-down / touch-start, `stop()` on
 * release. While listening, `transcript` updates live.
 */
export function useDictation({ lang = "en-AU" } = {}) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");

  const supported = typeof window !== "undefined" && !!(
    window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = useCallback(() => {
    if (!supported || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang           = lang;
    rec.continuous     = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let buf = "";
      for (let i = 0; i < e.results.length; i++) {
        buf += e.results[i][0].transcript;
      }
      setTranscript(buf.trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend   = () => setListening(false);
    setTranscript("");
    setListening(true);
    rec.start();
    recRef.current = rec;
  }, [supported, listening, lang]);

  const stop = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* */ }
    }
    setListening(false);
  }, []);

  return { listening, transcript, start, stop, supported };
}


/**
 * useWhisperDictation — press-and-hold mic input that uploads the
 * audio blob to /api/voice/stt (OpenAI Whisper via Emergent LLM).
 *
 * Why a second hook?  Web Speech API (`useDictation`) is free and
 * on-device but missing or flaky on iOS, in-app browsers, and Firefox.
 * Whisper handles all of those at the cost of one round-trip.
 *
 *   const { listening, transcript, start, stop, transcribing,
 *           supported, error }
 *     = useWhisperDictation({ lang: "en" });
 *
 * `start()` opens the mic and begins recording. `stop()` ends the
 * recording AND fires the upload — `transcript` populates when the
 * server replies. `transcribing` is true during the upload so the
 * UI can show a spinner.
 */
export function useWhisperDictation({ lang = "" } = {}) {
  const mediaRef    = useRef(null);
  const chunksRef   = useRef([]);
  const streamRef   = useRef(null);
  const [listening,    setListening]    = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript,   setTranscript]   = useState("");
  const [error,        setError]        = useState("");

  const supported = typeof window !== "undefined"
    && !!(navigator.mediaDevices && window.MediaRecorder);

  const start = useCallback(async () => {
    if (!supported || listening || transcribing) return;
    setError("");
    setTranscript("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        { audio: true });
      streamRef.current = stream;
      // Pick the best mime the browser supports
      const mime =
        ["audio/webm;codecs=opus", "audio/webm",
                "audio/mp4", "audio/ogg"]
          .find((m) => window.MediaRecorder.isTypeSupported?.(m))
        || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime })
                            : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      rec.start();
      mediaRef.current = rec;
      setListening(true);
    } catch (e) {
      setError(e?.message || "Microphone permission denied");
      setListening(false);
    }
  }, [supported, listening, transcribing]);

  const stop = useCallback(async () => {
    const rec = mediaRef.current;
    const stream = streamRef.current;
    if (!rec) { setListening(false); return; }
    const done = new Promise((res) => { rec.onstop = res; });
    try { rec.stop(); } catch { /* */ }
    setListening(false);
    await done;
    // Close mic
    if (stream) {
      try { stream.getTracks().forEach((t) => t.stop()); }
      catch { /* */ }
    }
    const blob = new Blob(chunksRef.current,
      { type: rec.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (!blob.size) return;
    setTranscribing(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const ext  = (blob.type.split("/")[1] || "webm")
                          .split(";")[0];
      const fd   = new FormData();
      fd.append("audio", blob, `clip.${ext}`);
      if (lang) fd.append("language", lang.slice(0, 5));
      const resp = await fetch(`${base}/api/voice/stt`, {
        method:      "POST",
        credentials: "include",
        body:        fd,
      });
      if (!resp.ok) throw new Error(`STT HTTP ${resp.status}`);
      const data = await resp.json();
      setTranscript((data?.text || "").trim());
    } catch (e) {
      setError(e?.message || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  }, [lang]);

  // Safety: release the mic if the component unmounts mid-record
  useEffect(() => () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); }
      catch { /* */ }
    }
  }, []);

  return { listening, transcribing, transcript, start, stop,
              supported, error };
}
